import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { getOverview, getFeaturedVineyards, getFeaturedWines } from '../lib/api';
import { StarRating, WineScore } from '../components/StarRating';
import { MapPin, Wine, ArrowRight, Grape, Clock } from 'lucide-react';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ vineyards: 0, wines: 0, users: 0 });
  const [featuredVineyards, setFeaturedVineyards] = useState([]);
  const [featuredWines, setFeaturedWines] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [overview, vineyards, wines] = await Promise.all([
        getOverview().catch(() => ({ vineyards: 0, wines: 0, users: 0 })),
        getFeaturedVineyards(3).catch(() => []),
        getFeaturedWines(4).catch(() => []),
      ]);
      setStats(overview || { vineyards: 0, wines: 0, users: 0 });
      setFeaturedVineyards(Array.isArray(vineyards) ? vineyards : []);
      setFeaturedWines(Array.isArray(wines) ? wines : []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setFeaturedVineyards([]);
      setFeaturedWines([]);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1760681554226-197f4c977e42?w=1920&q=80')`,
          }}
        />
        <div className="hero-overlay" />
        <div className="hero-content text-center px-4 max-w-4xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#d4af37] mb-6 animate-fade-in-up">
            Discover Excellence
          </p>
          <h1 className="text-5xl md:text-7xl font-serif font-medium tracking-tight text-stone-50 mb-6 animate-fade-in-up animation-delay-100">
            The World's Finest
            <br />
            <span className="text-[#d4af37]">Vineyards & Wines</span>
          </h1>
          <p className="text-lg md:text-xl text-stone-400 font-light mb-10 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            Explore, rate, and curate your personal collection of exceptional wines 
            from prestigious vineyards around the globe.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-300">
            <Button
              onClick={() => navigate('/vineyards')}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest font-bold text-xs px-8 py-6 luxury-btn"
              data-testid="explore-vineyards-btn"
            >
              Explore Vineyards
            </Button>
            {!isAuthenticated && (
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="bg-transparent border border-stone-700 text-stone-300 hover:border-[#d4af37] hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs px-8 py-6"
                data-testid="join-community-btn"
              >
                Join the Community
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Featured Vineyards */}
      <section className="py-20" data-testid="featured-vineyards-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Curated Selection</p>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-50">Featured Vineyards</h2>
            </div>
            <Link
              to="/vineyards"
              className="hidden md:flex items-center gap-2 text-stone-400 hover:text-[#d4af37] transition-colors"
              data-testid="view-all-vineyards-link"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {featuredVineyards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredVineyards.map((vineyard, index) => (
                <Link
                  key={vineyard.id}
                  to={`/vineyards/${vineyard.id}`}
                  className="vineyard-card group relative overflow-hidden bg-stone-900/40 border border-white/5"
                  data-testid={`featured-vineyard-${index}`}
                >
                  <div className="aspect-[4/3] img-zoom">
                    <img
                      src={vineyard.photos?.[0] || 'https://images.unsplash.com/photo-1695700762236-3c61c5026858?w=600&q=80'}
                      alt={vineyard.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      {vineyard.region}, {vineyard.country}
                    </div>
                    <h3 className="text-xl font-serif text-stone-50 mb-2 group-hover:text-[#d4af37] transition-colors">
                      {vineyard.name}
                    </h3>
                    <p className="text-sm text-stone-500 mb-4">{vineyard.producer}</p>
                    <div className="flex items-center justify-between">
                      <StarRating rating={vineyard.average_rating || 0} readonly size="sm" />
                      <span className="text-xs text-stone-600">
                        {vineyard.rating_count} reviews
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <MapPin className="w-12 h-12 text-stone-700 mx-auto mb-4" />
              <p className="text-stone-500">No vineyards yet. Be the first to add one!</p>
              {isAuthenticated && (
                <Button
                  onClick={() => navigate('/vineyards/new')}
                  className="mt-4 bg-[#d4af37] text-stone-950"
                  data-testid="add-first-vineyard-btn"
                >
                  Add Vineyard
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Wines */}
      <section className="py-20 bg-stone-900/20" data-testid="featured-wines-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Top Rated</p>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-50">Exceptional Wines</h2>
            </div>
            <Link
              to="/wines"
              className="hidden md:flex items-center gap-2 text-stone-400 hover:text-[#d4af37] transition-colors"
              data-testid="view-all-wines-link"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {featuredWines.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredWines.map((wine, index) => (
                <Link
                  key={wine.id}
                  to={`/wines/${wine.id}`}
                  className="wine-card group p-6 bg-stone-900/20 hover:bg-stone-900/60 border border-white/5 transition-all duration-300"
                  data-testid={`featured-wine-${index}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-widest text-[#d4af37] mb-1">
                        {wine.grape_variety}
                      </p>
                      <h3 className="text-lg font-serif text-stone-50 group-hover:text-[#d4af37] transition-colors">
                        {wine.name}
                      </h3>
                    </div>
                    {wine.average_rating > 0 && (
                      <WineScore score={wine.average_rating} size="sm" />
                    )}
                  </div>
                  <p className="text-sm text-stone-500 mb-2">{wine.vineyard_name}</p>
                  {wine.vintage && (
                    <p className="text-xs text-stone-600">{wine.vintage}</p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Wine className="w-12 h-12 text-stone-700 mx-auto mb-4" />
              <p className="text-stone-500">No wines yet. Add your first wine!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="py-24" data-testid="cta-section">
          <div className="container mx-auto px-4 md:px-8 text-center">
            <h2 className="text-3xl md:text-5xl font-serif text-stone-50 mb-6">
              Begin Your Wine Journey
            </h2>
            <p className="text-lg text-stone-400 mb-10 max-w-xl mx-auto">
              Create your account to rate wines, save favorites, and contribute to our growing 
              community of wine enthusiasts.
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest font-bold text-xs px-10 py-6"
              data-testid="cta-register-btn"
            >
              Create Free Account
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
